import React, {
  useEffect,
  useState,
} from "react";
import "../../styles/ticket.scss";
import {
  tryFetch,
  IResponse,
} from "../../helpers/FetchAPI";
import { getTokenOrDefault } from "../../helpers/Storage";
import { Link } from "react-router-dom";
import TicketFilter from "./TicketFilter";

interface Order {
  id: string;
  order_reference: string;
  event_title: string;
  event_image: string;
  selected_date: string;
  selected_time: string;
  event_venue: string;
  total_amount: number;
  ticket_count: number;
  ticket_url: string;
  ticket_type: string;
  created_at: string;
  status: string;
  event_date_raw: string;
}

interface SelectedDateDetails {
  selectedDate: string;
  selectedTime: string;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const parseApiDateTime = (
  value: string
): Date | null => {
  if (!value) {
    return null;
  }

  const match = value
    .trim()
    .match(
      /^(\d{4})-(\d{2})-(\d{2})(?:\s+|T)(\d{2}):(\d{2})(?::(\d{2}))?$/
    );

  if (!match) {
    const fallbackDate = new Date(
      value.replace(/-/g, "/")
    );

    return Number.isNaN(
      fallbackDate.getTime()
    )
      ? null
      : fallbackDate;
  }

  const [
    ,
    year,
    month,
    day,
    hour,
    minute,
    second = "00",
  ] = match;

  const parsedDate = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second)
  );

  return Number.isNaN(
    parsedDate.getTime()
  )
    ? null
    : parsedDate;
};

const extractSelectedDates = (
  value: unknown
): Date[] => {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) =>
        extractSelectedDates(item)
      )
      .filter(
        (date, index, array) =>
          array.findIndex(
            (existingDate) =>
              existingDate.getTime() ===
              date.getTime()
          ) === index
      )
      .sort(
        (firstDate, secondDate) =>
          firstDate.getTime() -
          secondDate.getTime()
      );
  }

  if (
    typeof value !== "string" ||
    !value.trim()
  ) {
    return [];
  }

  /*
   * Supports:
   *
   * 2027-05-27 10:00:00
   *
   * 2027-05-27 10:00:00
   * 2027-05-29 10:00:00
   *
   * 2027-05-27 10:00:00,
   * 2027-05-29 10:00:00
   */
  const dateMatches =
    value.match(
      /\d{4}-\d{2}-\d{2}(?:\s+|T)\d{2}:\d{2}(?::\d{2})?/g
    ) || [];

  const valuesToParse =
    dateMatches.length > 0
      ? dateMatches
      : [value.trim()];

  return valuesToParse
    .map(parseApiDateTime)
    .filter(
      (date): date is Date =>
        date !== null
    )
    .filter(
      (date, index, array) =>
        array.findIndex(
          (existingDate) =>
            existingDate.getTime() ===
            date.getTime()
        ) === index
    )
    .sort(
      (firstDate, secondDate) =>
        firstDate.getTime() -
        secondDate.getTime()
    );
};

const formatDate = (
  date: Date
): string => {
  return `${
    MONTH_NAMES[date.getMonth()]
  } ${date.getDate()}, ${date.getFullYear()}`;
};

const formatTime = (
  date: Date
): string => {
  return date.toLocaleTimeString(
    "en-US",
    {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }
  );
};

const formatSelectedDateDetails = (
  value: unknown
): SelectedDateDetails => {
  const dates =
    extractSelectedDates(value);

  if (dates.length === 0) {
    return {
      selectedDate: "N/A",
      selectedTime: "N/A",
    };
  }

  /*
   * Remove duplicate calendar dates.
   * Example:
   * May 27, 2027 10:00 AM
   * May 27, 2027 01:00 PM
   *
   * Selected Date remains:
   * May 27, 2027
   */
  const uniqueCalendarDates =
    dates.filter(
      (date, index, array) => {
        const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

        return (
          array.findIndex(
            (existingDate) =>
              `${existingDate.getFullYear()}-${existingDate.getMonth()}-${existingDate.getDate()}` ===
              dateKey
          ) === index
        );
      }
    );

  const firstDate =
    uniqueCalendarDates[0];

  const lastDate =
    uniqueCalendarDates[
      uniqueCalendarDates.length - 1
    ];

  const selectedDate =
    uniqueCalendarDates.length === 1
      ? formatDate(firstDate)
      : `${formatDate(
          firstDate
        )} - ${formatDate(lastDate)}`;

  const uniqueTimes = dates
    .map(formatTime)
    .filter(
      (time, index, array) =>
        array.indexOf(time) === index
    );

  return {
    selectedDate,
    selectedTime:
      uniqueTimes.length > 0
        ? uniqueTimes.join(", ")
        : "N/A",
  };
};

const formatTicketTypes = (
  value: unknown
): string => {
  if (Array.isArray(value)) {
    const ticketTypes = value
      .flatMap((item) =>
        typeof item === "string"
          ? item.split(
              /[,;|\n]+/
            )
          : []
      )
      .map((item) => item.trim())
      .filter(Boolean)
      .filter(
        (item, index, array) =>
          array.findIndex(
            (existingItem) =>
              existingItem.toLowerCase() ===
              item.toLowerCase()
          ) === index
      );

    return ticketTypes.length > 0
      ? ticketTypes.join(", ")
      : "N/A";
  }

  if (
    typeof value !== "string" ||
    !value.trim()
  ) {
    return "N/A";
  }

  const ticketTypes = value
    .split(/[,;|\n]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .filter(
      (item, index, array) =>
        array.findIndex(
          (existingItem) =>
            existingItem.toLowerCase() ===
            item.toLowerCase()
        ) === index
    );

  return ticketTypes.length > 0
    ? ticketTypes.join(", ")
    : "N/A";
};

const parseCreatedAt = (
  dateString: string
): Date => {
  if (!dateString) {
    return new Date();
  }

  /*
   * Supports:
   * 2026-06-24 10:19 AM
   */
  const match = dateString
    .trim()
    .match(
      /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i
    );

  if (match) {
    const [
      ,
      year,
      month,
      day,
      rawHour,
      minute,
      meridiem,
    ] = match;

    let hour = Number(rawHour);

    if (
      meridiem.toUpperCase() ===
        "PM" &&
      hour !== 12
    ) {
      hour += 12;
    }

    if (
      meridiem.toUpperCase() ===
        "AM" &&
      hour === 12
    ) {
      hour = 0;
    }

    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      hour,
      Number(minute)
    );
  }

  const fallbackDate = new Date(
    dateString.replace(/-/g, "/")
  );

  return Number.isNaN(
    fallbackDate.getTime()
  )
    ? new Date()
    : fallbackDate;
};

const TicketPage: React.FC = () => {
  const [orders, setOrders] =
    useState<Order[]>([]);

  const [timers, setTimers] =
    useState<{
      [key: string]: string;
    }>({});

  const [
    activeFilter,
    setActiveFilter,
  ] = useState("ALL");

  useEffect(() => {
    const fetchTickets =
      async () => {
        try {
          const token =
            getTokenOrDefault();

          const res: IResponse =
            await tryFetch({
              url: "/sec/ticket-orders",
              method: "GET",
              token,
            });

          if (
            res.is_success &&
            Array.isArray(res.data)
          ) {
            const mappedOrders:
              Order[] = (
              res.data as any[]
            ).map((item) => {
              const selectedDateDetails =
                formatSelectedDateDetails(
                  item.selected_dates
                );

              return {
                id: item.id,

                order_reference:
                  item.order_reference ||
                  "N/A",

                event_title:
                  item.event_title ||
                  "Untitled Event",

                event_image:
                  item.event_image || "",

                selected_date:
                  selectedDateDetails.selectedDate,

                selected_time:
                  selectedDateDetails.selectedTime,

                event_date_raw:
                  item.event_start_date ||
                  "",

                event_venue:
                  item.event_venue ||
                  "N/A",

                total_amount:
                  Number(
                    item.order_total ||
                      0
                  ),

                ticket_count:
                  Number(
                    item.ticket_count ||
                      0
                  ),

                ticket_url:
                  item.ticket_url || "",

                ticket_type:
                  formatTicketTypes(
                    item.ticket_type
                  ),

                created_at:
                  item.created_at || "",

                status:
                  item.status || "",
              };
            });

            setOrders(
              mappedOrders
            );
          } else {
            console.error(
              "Failed to fetch tickets:",
              res.error
            );
          }
        } catch (err) {
          console.error(
            "Error fetching tickets:",
            err
          );
        }
      };

    fetchTickets();
  }, []);

  useEffect(() => {
    const interval = setInterval(
      () => {
        const newTimers: {
          [key: string]: string;
        } = {};

        orders.forEach(
          (order) => {
            if (
              order.status ===
              "PENDING"
            ) {
              const createdAt =
                parseCreatedAt(
                  order.created_at
                );

              const expireTime =
                createdAt.getTime() +
                15 * 60 * 1000;

              const now =
                Date.now();

              const difference =
                expireTime - now;

              if (
                difference > 0
              ) {
                const minutes =
                  Math.floor(
                    difference /
                      1000 /
                      60
                  );

                const seconds =
                  Math.floor(
                    (difference /
                      1000) %
                      60
                  );

                newTimers[
                  order.id
                ] = `${minutes}:${seconds
                  .toString()
                  .padStart(
                    2,
                    "0"
                  )}`;
              } else {
                newTimers[
                  order.id
                ] = "";
              }
            }
          }
        );

        setTimers(newTimers);
      },
      1000
    );

    return () =>
      clearInterval(interval);
  }, [orders]);

  const filteredOrders =
    orders.filter((order) => {
      const today = new Date();

      const eventDate =
        parseApiDateTime(
          order.event_date_raw
        );

      if (
        activeFilter === "TO PAY"
      ) {
        return (
          order.status ===
          "PENDING"
        );
      }

      if (
        activeFilter === "PAID"
      ) {
        return (
          order.status ===
            "PAID" &&
          Boolean(
            eventDate &&
              eventDate >= today
          )
        );
      }

      if (
        activeFilter ===
        "COMPLETED"
      ) {
        return (
          order.status ===
            "PAID" &&
          Boolean(
            eventDate &&
              eventDate < today
          )
        );
      }

      if (
        activeFilter ===
        "CANCELLED"
      ) {
        return (
          order.status ===
          "CANCELLED"
        );
      }

      return (
        order.status ===
          "PENDING" ||
        order.status === "PAID"
      );
    });

  return (
    <div className="ticket-page">
      <h2>My Tickets</h2>

      <hr className="ticket-divider" />

      <TicketFilter
        activeFilter={
          activeFilter
        }
        setActiveFilter={
          setActiveFilter
        }
      />

      {filteredOrders.length ===
      0 ? (
        <div className="no-tickets">
          <p>No tickets found.</p>
        </div>
      ) : (
        filteredOrders.map(
          (order) => (
            <div
              className="ticket-card"
              key={order.id}
            >
              <div className="event-section">
                <img
                  src={
                    order.event_image
                  }
                  alt={
                    order.event_title
                  }
                  className="event-img"
                />

                <div className="timer-container">
                  {order.status ===
                    "PENDING" &&
                    timers[
                      order.id
                    ] && (
                      <p className="timer">
                        {
                          timers[
                            order.id
                          ]
                        }
                      </p>
                    )}
                </div>
              </div>

              <div className="ticket-details">
                <small className="created-at">
                  {order.created_at}
                </small>

                <h3 className="event-title">
                  {
                    order.event_title
                  }
                </h3>

                <p className="event-meta">
                  {order.event_venue}
                </p>

                <hr />

                <div className="ticket-information">
                  <p>
                    <strong>
                      Reference ID:
                    </strong>{" "}
                    {
                      order.order_reference
                    }
                  </p>

                  <p>
                    <strong>
                      Selected Date:
                    </strong>{" "}
                    {
                      order.selected_date
                    }
                  </p>

                  <p>
                    <strong>
                      Time:
                    </strong>{" "}
                    {
                      order.selected_time
                    }
                  </p>

                  <p>
                    <strong>
                      Ticket Count:
                    </strong>{" "}
                    {
                      order.ticket_count
                    }
                  </p>

                  <p>
                    <strong>
                      Ticket Type:
                    </strong>{" "}
                    {
                      order.ticket_type
                    }
                  </p>

                  <p>
                    <strong>
                      Total Amount:
                    </strong>{" "}
                    ₱
                    {Number(
                      order.total_amount ||
                        0
                    ).toLocaleString(
                      "en-PH",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }
                    )}
                  </p>
                </div>

                {order.status ===
                  "PENDING" && (
                  <Link
                    to={`/preview/${order.id}`}
                    className="download-btn"
                  >
                    Pay Now
                  </Link>
                )}

                {order.status ===
                  "PAID" &&
                  order.ticket_url && (
                    <a
                      href={
                        order.ticket_url
                      }
                      className="download-btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download Ticket
                    </a>
                  )}
              </div>
            </div>
          )
        )
      )}
    </div>
  );
};

export default TicketPage;